import React, { useState, useEffect } from "react";

// Material UI
import {
  Container,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Alert,
  Button,
} from "@mui/material";
import { Clear, InfoOutlined, ExpandMore } from "@mui/icons-material";

// Common
import {
  DraggableVariable,
  FormikDraggableTextField,
} from "../DraggableVariables";
import { messageVariables } from "./MessageValues";
import { MessagePreview } from "./MessagePreview";

// Styles
import { textStyles, commonStyles, buttonStyles } from "Common/Styles";
import { grey } from "Constants/ColourConstants";

const alertMessages = {
  success: "Done",
  error: "Needs attention",
  info: "TO-DO",
};

export const MessageEditor = ({ formik, field, formikValue }) => {
  const enValue = formik.values[formikValue]?.[field.id]?.en || "";
  const frValue = formik.values[formikValue]?.[field.id]?.fr || "";
  const enError = formik.errors?.[formikValue]?.[field.id]?.en;
  const frError = formik.errors?.[formikValue]?.[field.id]?.fr;
  const enTouched = formik.touched?.[formikValue]?.[field.id]?.en;
  const frTouched = formik.touched?.[formikValue]?.[field.id]?.fr;

  const [status, setStatus] = useState("info");

  useEffect(() => {
    if (enValue === "" && frValue === "") {
      setStatus("info");
      return;
    }

    if ((enTouched && enError) || (frTouched && frError)) {
      setStatus("error");
    } else if (!enError && !frError) {
      setStatus("success");
    } else {
      setStatus("info");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enTouched, enError, frTouched, frError]);

  const handleClearText = () => {
    formik.setFieldValue(`${formikValue}.${field.id}.en`, "");
    formik.setFieldValue(`${formikValue}.${field.id}.fr`, "");
    formik.setFieldTouched(`${formikValue}.${field.id}.en`, false);
    formik.setFieldTouched(`${formikValue}.${field.id}.fr`, false);
    setStatus("info");
  };

  return (
    <Container disableGutters sx={commonStyles.paperContainer}>
      <Accordion
        defaultExpanded
        square={false}
        sx={{ background: "transparent", boxShadow: "none" }}
      >
        <AccordionSummary sx={{ height: "65px" }} expandIcon={<ExpandMore />}>
          <Stack
            sx={{ width: "100%" }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={textStyles.subtitle}>{field.label}</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Alert severity={status}>{alertMessages[status]}</Alert>
              <Tooltip title={field.helperText} arrow>
                <InfoOutlined sx={{ color: grey }} />
              </Tooltip>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography sx={textStyles.subtitle2}>Text Editor</Typography>
              <Button
                type="button"
                variant="text"
                sx={buttonStyles.cancelButton}
                startIcon={<Clear />}
                onClick={handleClearText}
              >
                Clear Text
              </Button>
            </Stack>
            <Stack
              direction="row"
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              {messageVariables.map((v) => (
                <DraggableVariable key={v.name} name={v.name} label={v.label} />
              ))}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <FormikDraggableTextField
                key={`${field.id}-en`}
                formik={formik}
                field={field}
                language="en"
                label="English"
                formikValue={formikValue}
              />
              <FormikDraggableTextField
                key={`${field.id}-fr`}
                formik={formik}
                field={field}
                language="fr"
                label="French"
                sx={{ mt: 2 }}
                formikValue={formikValue}
              />
            </Stack>
            <MessagePreview
              formik={formik}
              field={field}
              formikValue={formikValue}
              clinic={formik.values}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};
